/**
 * The player's un-acknowledged equip intent, kept separately from server state.
 *
 * Same shape as the building roster overlay (`land/pendingRosterOps.ts`), and for the same reason:
 * a drop is up to four independent POSTs, and the reply to the first one is genuinely the newest
 * thing the server has said *and* genuinely reports an empty slot, because the follow-up equip has
 * not been sent yet. The payload is not stale relative to other payloads — it is stale relative to
 * the player's intent, and intent has to live somewhere in the data model.
 *
 * So the slots are derived rather than stored:
 *
 *     displayed = replayWeaponOps(pendingWeaponOps, character.embedWeapons)
 *
 * Server state is then free to be overwritten by any refetch, in any order, with no guards — it is
 * not what is on screen. An operation leaves the queue only when the server acknowledges it (folded
 * into `embedWeapons` in the same action, so nothing flickers) or rejects it (dropped, and its
 * effect simply stops being replayed).
 */

/** The weapon row backing a slot — endpoints hand back slightly different shapes, so it stays open. */
export type PendingWeaponData = Record<string, any>

/** One entry of `character.embedWeapons`; `weaponId` is populated on a fetched character. */
export type EmbedWeaponSlot = {
  order: number
  weaponId: PendingWeaponData | string
} & Record<string, any>

export type PendingWeaponOp = {
  /** Client-generated; the acknowledgement refers back to it. */
  id: string
  characterId: string
  /** Slot index. Both kinds are keyed by slot alone: the server stores one embed per order. */
  order: number
} & (
  /**
   * `weapon` is what the slot was holding. Optional only so a caller that cannot name it still
   * works — without it the copy is not put back in the inventory list until the next fetch.
   */
  | { kind: 'unequip'; weapon?: PendingWeaponData }
  /** `weapon` is the inventory row, captured at enqueue time so the slot renders like a fetched one. */
  | { kind: 'equip'; weapon: PendingWeaponData }
)

let opCounter = 0

export function nextWeaponOpId(): string {
  opCounter += 1
  return `weapon-op-${opCounter}`
}

export function opTargetsCharacter(op: PendingWeaponOp, characterId: string): boolean {
  return String(op.characterId) === String(characterId)
}

/** Apply one operation to a character's embedded weapons, returning a new array. */
export function applyWeaponOp(
  embedWeapons: readonly EmbedWeaponSlot[],
  op: PendingWeaponOp,
): EmbedWeaponSlot[] {
  // An equip displaces whatever holds the slot, matching how the server keys an embed by `order`.
  const rest = (embedWeapons as EmbedWeaponSlot[]).filter((e) => Number(e?.order) !== op.order)
  if (op.kind === 'unequip') return rest
  return rest.concat([{ order: op.order, weaponId: op.weapon }])
}

/** Replay every pending operation for one character on top of the server's embeds. */
export function replayWeaponOps(
  ops: readonly PendingWeaponOp[],
  serverEmbedWeapons: readonly EmbedWeaponSlot[],
  characterId: string,
): EmbedWeaponSlot[] {
  let embeds = serverEmbedWeapons as EmbedWeaponSlot[]
  let touched = false
  for (const op of ops) {
    if (!opTargetsCharacter(op, characterId)) continue
    embeds = applyWeaponOp(embeds, op)
    touched = true
  }
  // Identity is preserved when nothing applies, so memos and selectors don't churn.
  return touched ? embeds : (serverEmbedWeapons as EmbedWeaponSlot[])
}

function weaponRowId(weapon: PendingWeaponData | null | undefined): string {
  return String(weapon?._id ?? '')
}

/**
 * Apply one operation to the inventory list — the same overlay, seen from the other side.
 *
 * A copy the player just dropped into a slot has to leave the list on that frame, and a copy they
 * just took off has to come back, or the list contradicts the slots and invites the player to drag
 * a copy they have already used. Availability is what moves: the backend only lists rows it still
 * has spare copies of, so a row whose last spare copy is now worn drops out entirely.
 *
 * Used for both the replay and the fold on acknowledgement, so the two cannot drift.
 */
export function applyWeaponInventoryOp<T extends PendingWeaponData>(
  rows: readonly T[],
  op: PendingWeaponOp,
): T[] {
  const id = weaponRowId(op.weapon)
  if (!id) return rows as T[]
  const delta = op.kind === 'equip' ? -1 : 1

  let listed = false
  const next: T[] = []
  for (const row of rows) {
    if (weaponRowId(row) !== id) {
      next.push(row)
      continue
    }
    listed = true
    const base =
      row.availableStock !== undefined ? Number(row.availableStock) : Number(row.stock ?? 1)
    const available = base + delta
    if (available <= 0) continue
    next.push({ ...row, availableStock: available })
  }

  /**
   * A weapon whose copies were all worn has no server row at all, so taking one off has to rebuild
   * one. `stock` is only carried on a row that came from the inventory; a copy dragged off a slot
   * does not know how many siblings it has, and reads as a single copy until the next fetch.
   */
  if (!listed && delta > 0) {
    next.push({
      ...(op.weapon as PendingWeaponData),
      stock: Number((op.weapon as PendingWeaponData).stock ?? delta),
      availableStock: delta,
    } as unknown as T)
  }

  return next
}

/** Replay every pending operation for one character on top of the server's inventory list. */
export function replayWeaponInventory<T extends PendingWeaponData>(
  ops: readonly PendingWeaponOp[],
  serverWeapons: readonly T[],
  characterId: string,
): T[] {
  let rows = serverWeapons as T[]
  let touched = false
  for (const op of ops) {
    if (!opTargetsCharacter(op, characterId)) continue
    rows = applyWeaponInventoryOp(rows, op)
    touched = true
  }
  // Identity is preserved when nothing applies, so memos don't churn.
  return touched ? rows : (serverWeapons as T[])
}

/**
 * A character row with its pending equips replayed on top. Returns the same object when nothing is
 * pending — callers memo on the result, and anything reading equipped stats (the stats panel's
 * weapon bonuses) would otherwise recompute on every store tick.
 */
export function withPendingWeapons<T extends Record<string, any>>(
  character: T | null | undefined,
  ops: readonly PendingWeaponOp[],
  characterId: string,
): T | null | undefined {
  if (!character || ops.length === 0) return character
  const server = (character.embedWeapons ?? []) as EmbedWeaponSlot[]
  const replayed = replayWeaponOps(ops, server, characterId)
  if (replayed === server) return character
  return { ...character, embedWeapons: replayed }
}
